/**
 * Starts PDP gallery videos only when they are close to being seen.
 *
 * Gallery videos are rendered deferred (snippets/video.liquid `video_defer`): the <video> sits in the
 * <deferred-media> <template>, so nothing downloads at page load and the poster photo shows meanwhile. This
 * loads it, through the theme's own DeferredMedia.loadContent(), once it is near the viewport (and only after
 * the page has finished loading). The video then autoplays muted and looping exactly as before.
 *
 * If this script never runs the poster simply stays, and a click on it still plays the video.
 */
const SELECTOR = 'deferred-media[data-lazy-video]';
// The gallery tile is ~460px wide, so the 720p file is plenty; the theme's Liquid only exposes the 1080p one.
const MAX_HEIGHT = 720;

/** @type {Promise<any> | null} */
let productJson = null;

/** The product's JSON feed (it lists every video rendition), fetched once and only when a video is about to load. */
const loadProduct = () => {
  const handle = location.pathname.match(/\/products\/([^/?#]+)/)?.[1];
  if (!handle) return Promise.resolve(null);
  productJson ??= fetch(`/products/${handle}.js`)
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null);
  return productJson;
};

/**
 * Points the not-yet-loaded <video> in `el`'s template at the largest mp4 up to MAX_HEIGHT. Any problem (no
 * data, no match, slow feed) leaves the stock 1080p source in place, so the video still plays.
 *
 * @param {Element} el - The <deferred-media> element.
 */
const preferSmallerRendition = async (el) => {
  try {
    const mediaId = el.closest('.product-media')?.getAttribute('data-media-id');
    const source = el.querySelector('template')?.content.querySelector('source');
    if (!mediaId || !source) return;

    const product = await Promise.race([loadProduct(), new Promise((resolve) => setTimeout(() => resolve(null), 800))]);
    const media = product?.media?.find((m) => String(m.id) === mediaId);
    const mp4s = (media?.sources || []).filter((s) => s.format === 'mp4' && s.height <= MAX_HEIGHT);
    mp4s.sort((a, b) => b.height - a.height);
    if (mp4s[0]?.url) source.setAttribute('src', mp4s[0].url);
  } catch (e) {
    /* keep the stock source */
  }
};

if (!window.__seneGalleryVideoLazy) {
  window.__seneGalleryVideoLazy = true;

  /** @type {IntersectionObserver | null} */
  let observer = null;
  const seen = new WeakSet();

  /** @param {Element} el */
  const watch = (el) => {
    if (!observer || seen.has(el) || el.hasAttribute('data-media-loaded')) return;
    seen.add(el);
    observer.observe(el);
  };

  const arm = () => {
    try {
      observer = new IntersectionObserver(
        async (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const el = /** @type {any} */ (entry.target);
            observer?.unobserve(el);
            await preferSmallerRendition(el);
            if (el.isConnected && typeof el.loadContent === 'function') el.loadContent(false);
          }
        },
        { rootMargin: '400px 0px' }
      );

      for (const el of document.querySelectorAll(SELECTOR)) watch(el);

      // The server replaces the whole gallery on a colour change; pick up its video too.
      new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (!(node instanceof Element)) continue;
            if (node.matches(SELECTOR)) watch(node);
            for (const el of node.querySelectorAll(SELECTOR)) watch(el);
          }
        }
      }).observe(document.querySelector('main') || document.body, { childList: true, subtree: true });
    } catch (e) {
      /* never interfere with the page */
    }
  };

  const armAfterLoad = () => setTimeout(arm, 1000);
  if (document.readyState === 'complete') armAfterLoad();
  else window.addEventListener('load', armAfterLoad, { once: true });
}
